import { authSelectors } from 'ducks/auth'
import { orderSelectors, setCartDeals } from 'ducks/orderComponents'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import restClient from 'restClient'
import { Deal } from '../types'

const restClientInstance = restClient(window.API_ROOT + '/api')

function isAbortError(error: unknown, signal: AbortSignal): boolean {
  return (error as { name?: string })?.name === 'AbortError' || signal.aborted
}

/**
 * Fetches cart-level deals (deals with cart_subtotal, cart_quantity, or item_quantity conditions)
 * from the backend. These deals are not attached to individual product ordering_v2 entries
 * because they apply at the cart level rather than to specific products.
 *
 * Results are cached in orderComponents Redux state for the current org session.
 * Cache is cleared on org switch via SET_ORG_ID (same pattern as wallet).
 */
export const useCartDeals = (): { cartDeals: Deal[] } => {
  const dispatch = useDispatch()
  const orgId = useSelector(authSelectors.getOrgId)
  const cartDeals = useSelector(orderSelectors.getCartDeals)

  useEffect(() => {
    if (!orgId || cartDeals !== undefined) return

    const controller = new AbortController()
    const { signal } = controller

    const load = async () => {
      try {
        const res = await restClientInstance('CUSTOM_GET', 'custom', {
          url: `orgs/${orgId}/deals/cart-deals/`,
          signal,
        })
        if (signal.aborted) return
        const data = (res?.data || res?.json || res || {}) as Record<string, Deal[]>
        const allDeals: Deal[] = []
        for (const distKey of Object.keys(data)) {
          const distDeals = data[distKey]
          if (Array.isArray(distDeals)) {
            allDeals.push(...distDeals)
          }
        }
        dispatch(setCartDeals(allDeals))
      } catch (error) {
        if (isAbortError(error, signal)) return
        dispatch(setCartDeals([]))
      }
    }

    load()

    return () => controller.abort()
  }, [orgId, cartDeals, dispatch])

  return useMemo(() => ({ cartDeals: cartDeals ?? [] }), [cartDeals])
}
